import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  IoPeopleOutline,
  IoBusinessOutline,
  IoArrowDownOutline,
  IoHelpCircleOutline
} from 'react-icons/io5';

// Info Popover Component for showing calculation formulas
const InfoPopover = ({ title, formula, isOpen, onToggle }) => {
  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="text-gray-400 hover:text-gray-600 transition-colors ml-1"
      >
        <IoHelpCircleOutline className="w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={onToggle}
          ></div>
          <div className="absolute right-0 top-5 z-20 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-3">
            <h6 className="font-medium text-gray-800 text-xs mb-2">{title}</h6>
            <div className="text-xs text-gray-600 space-y-2">
              {formula.map((line, idx) => (
                <div key={idx} className={line.type === 'formula' ? 'font-mono bg-gray-50 p-1 rounded' : ''}>
                  {line.text}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const GlobalAnalytics = ({ data }) => {
  const [demographicsExpanded, setDemographicsExpanded] = useState(false);
  const [interactionExpanded, setInteractionExpanded] = useState(false);
  const [interactionSortBy, setInteractionSortBy] = useState('positive');
  const [activePopover, setActivePopover] = useState(null);
  const [globalDemographics, setGlobalDemographics] = useState(null);
  const [globalInteractions, setGlobalInteractions] = useState(null);

  // Get calculation formula for different insight types
  const getInsightFormula = (insightType) => {
    const formulas = {
      'age_satisfaction': [
        { type: 'text', text: 'Impact Level Calculation:' },
        { type: 'formula', text: 'Difference = |Highest Age % - Lowest Age %|' },
        { type: 'text', text: 'If difference > 20% → High Impact' },
        { type: 'text', text: 'If difference ≤ 20% → Moderate Impact' },
        { type: 'text', text: 'Example: |45% - 15%| = 30% → High Impact' }
      ],
      'interaction_summary': [
        { type: 'text', text: 'Impact Level Calculation:' },
        { type: 'formula', text: 'Based on multiple factors:' },
        { type: 'text', text: '• High-volume channels with <30% positive' },
        { type: 'text', text: '• Overall average satisfaction <50%' },
        { type: 'text', text: '• Number of problematic channels (>2)' }
      ]
    };
    return formulas[insightType] || [{ type: 'text', text: 'Calculation details not available' }];
  };

  useEffect(() => {
    if (data && data.data) {
      analyzeGlobalData(data);
    }
  }, [data]);

  const analyzeGlobalData = (feedbackData) => {
    const allFeedback = feedbackData.data || [];
    
    // Global demographics analysis with key themes
    const globalDemo = analyzeGlobalDemographics(allFeedback);
    setGlobalDemographics(globalDemo);
    
    // Global interactions analysis with key themes
    const globalInt = analyzeGlobalInteractions(allFeedback);
    setGlobalInteractions(globalInt);
  };

  const analyzeGlobalDemographics = (allFeedback) => {
    const ageGroups = { 'Under 30': 0, '30-50': 0, 'Over 50': 0 };
    const genderDistribution = { Male: 0, Female: 0 };
    const ageSentiments = { 
      'Under 30': { positive: 0, negative: 0, neutral: 0 },
      '30-50': { positive: 0, negative: 0, neutral: 0 },
      'Over 50': { positive: 0, negative: 0, neutral: 0 }
    };
    const genderSentiments = {
      Male: { positive: 0, negative: 0, neutral: 0 },
      Female: { positive: 0, negative: 0, neutral: 0 }
    };

    // Track key themes by demographics with category-subcategory relationships
    const ageKeyThemes = {
      'Under 30': { 
        positiveCategories: {}, 
        negativeCategories: {}, 
        positiveSubcategories: {}, 
        negativeSubcategories: {},
        categorySubcategoryMap: {} // Track which subcategories belong to which categories
      },
      '30-50': { 
        positiveCategories: {}, 
        negativeCategories: {}, 
        positiveSubcategories: {}, 
        negativeSubcategories: {},
        categorySubcategoryMap: {}
      },
      'Over 50': { 
        positiveCategories: {}, 
        negativeCategories: {}, 
        positiveSubcategories: {}, 
        negativeSubcategories: {},
        categorySubcategoryMap: {}
      }
    };
    const genderKeyThemes = {
      Male: { positiveCategories: {}, negativeCategories: {}, positiveSubcategories: {}, negativeSubcategories: {} },
      Female: { positiveCategories: {}, negativeCategories: {}, positiveSubcategories: {}, negativeSubcategories: {} }
    };

    allFeedback.forEach(item => {
      const age = parseInt(item.age) || 0;
      const ageGroup = age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
      const gender = item.gender === 'M' ? 'Male' : 'Female';
      
      ageGroups[ageGroup]++;
      genderDistribution[gender]++;
      
      // Process categories and subcategories for key themes
      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          const categoryName = cat.category;
          const sentiment = cat.sentiment;
          
          // Track overall sentiment
          ageSentiments[ageGroup][sentiment]++;
          genderSentiments[gender][sentiment]++;
          
          // Track key themes by category
          if (sentiment === 'positive') {
            ageKeyThemes[ageGroup].positiveCategories[categoryName] = (ageKeyThemes[ageGroup].positiveCategories[categoryName] || 0) + 1;
            genderKeyThemes[gender].positiveCategories[categoryName] = (genderKeyThemes[gender].positiveCategories[categoryName] || 0) + 1;
          } else if (sentiment === 'negative') {
            ageKeyThemes[ageGroup].negativeCategories[categoryName] = (ageKeyThemes[ageGroup].negativeCategories[categoryName] || 0) + 1;
            genderKeyThemes[gender].negativeCategories[categoryName] = (genderKeyThemes[gender].negativeCategories[categoryName] || 0) + 1;
          }
        });
      }

      // Process subcategories for key themes with category linkage
      if (item.processed_data?.subcategories) {
        item.processed_data.subcategories.forEach(subcat => {
          if (subcat.subcategory && subcat.subcategory !== 'Others') {
            const subcategoryName = subcat.subcategory.replace(/_/g, ' ');
            const categoryName = subcat.category || 'Unknown';
            const sentiment = subcat.sentiment;
            
            // Initialize category in mapping if not exists
            if (!ageKeyThemes[ageGroup].categorySubcategoryMap[categoryName]) {
              ageKeyThemes[ageGroup].categorySubcategoryMap[categoryName] = {
                positive: {},
                negative: {}
              };
            }
            
            if (sentiment === 'positive') {
              ageKeyThemes[ageGroup].positiveSubcategories[subcategoryName] = (ageKeyThemes[ageGroup].positiveSubcategories[subcategoryName] || 0) + 1;
              genderKeyThemes[gender].positiveSubcategories[subcategoryName] = (genderKeyThemes[gender].positiveSubcategories[subcategoryName] || 0) + 1;
              
              // Track category-subcategory relationship
              ageKeyThemes[ageGroup].categorySubcategoryMap[categoryName].positive[subcategoryName] = 
                (ageKeyThemes[ageGroup].categorySubcategoryMap[categoryName].positive[subcategoryName] || 0) + 1;
                
            } else if (sentiment === 'negative') {
              ageKeyThemes[ageGroup].negativeSubcategories[subcategoryName] = (ageKeyThemes[ageGroup].negativeSubcategories[subcategoryName] || 0) + 1;
              genderKeyThemes[gender].negativeSubcategories[subcategoryName] = (genderKeyThemes[gender].negativeSubcategories[subcategoryName] || 0) + 1;
              
              // Track category-subcategory relationship
              ageKeyThemes[ageGroup].categorySubcategoryMap[categoryName].negative[subcategoryName] = 
                (ageKeyThemes[ageGroup].categorySubcategoryMap[categoryName].negative[subcategoryName] || 0) + 1;
            }
          }
        });
      }
    });

    // Calculate satisfaction percentages
    const ageGroupSatisfaction = Object.entries(ageSentiments).map(([ageGroup, sentiments]) => {
      const total = sentiments.positive + sentiments.negative + sentiments.neutral;
      
      // Get top key themes for this age group
      const positiveCategories = Object.entries(ageKeyThemes[ageGroup].positiveCategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([cat, count]) => ({ name: cat.replace(/_/g, ' '), count }));
      
      const negativeCategories = Object.entries(ageKeyThemes[ageGroup].negativeCategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([cat, count]) => ({ name: cat.replace(/_/g, ' '), count }));
      
      const positiveSubcategories = Object.entries(ageKeyThemes[ageGroup].positiveSubcategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([subcat, count]) => ({ name: subcat, count }));
      
      const negativeSubcategories = Object.entries(ageKeyThemes[ageGroup].negativeSubcategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([subcat, count]) => ({ name: subcat, count }));

      return {
        ageGroup,
        total,
        positive: sentiments.positive,
        negative: sentiments.negative,
        neutral: sentiments.neutral,
        positivePercentage: total > 0 ? Math.round((sentiments.positive / total) * 100) : 0,
        negativePercentage: total > 0 ? Math.round((sentiments.negative / total) * 100) : 0,
        neutralPercentage: total > 0 ? Math.round((sentiments.neutral / total) * 100) : 0,
        keyThemes: {
          positiveCategories,
          negativeCategories,
          positiveSubcategories,
          negativeSubcategories
        },
        categorySubcategoryMap: ageKeyThemes[ageGroup].categorySubcategoryMap
      };
    });

    const genderSatisfaction = Object.entries(genderSentiments).map(([gender, sentiments]) => {
      const total = sentiments.positive + sentiments.negative + sentiments.neutral;
      
      // Get top key themes for this gender
      const positiveCategories = Object.entries(genderKeyThemes[gender].positiveCategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([cat, count]) => ({ name: cat.replace(/_/g, ' '), count }));
      
      const negativeCategories = Object.entries(genderKeyThemes[gender].negativeCategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([cat, count]) => ({ name: cat.replace(/_/g, ' '), count }));
      
      const positiveSubcategories = Object.entries(genderKeyThemes[gender].positiveSubcategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([subcat, count]) => ({ name: subcat, count }));
      
      const negativeSubcategories = Object.entries(genderKeyThemes[gender].negativeSubcategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([subcat, count]) => ({ name: subcat, count }));

      return {
        gender,
        total,
        positive: sentiments.positive,
        negative: sentiments.negative,
        neutral: sentiments.neutral,
        positivePercentage: total > 0 ? Math.round((sentiments.positive / total) * 100) : 0,
        negativePercentage: total > 0 ? Math.round((sentiments.negative / total) * 100) : 0,
        neutralPercentage: total > 0 ? Math.round((sentiments.neutral / total) * 100) : 0,
        keyThemes: {
          positiveCategories,
          negativeCategories,
          positiveSubcategories,
          negativeSubcategories
        }
      };
    });

    // Generate global demographic insights with key themes
    const insights = [];
    const ageGroupsByPositive = [...ageGroupSatisfaction].sort((a, b) => b.positivePercentage - a.positivePercentage);
    const mostSatisfiedAge = ageGroupsByPositive[0];
    const leastSatisfiedAge = ageGroupsByPositive[ageGroupsByPositive.length - 1];
    
    if (mostSatisfiedAge && leastSatisfiedAge && mostSatisfiedAge.ageGroup !== leastSatisfiedAge.ageGroup) {
      // Build subcategories with category context from categorySubcategoryMap
      const enrichedPositiveSubcats = [];
      const enrichedNegativeSubcats = [];
      
      // Get subcategories with their parent categories for most satisfied age group
      if (mostSatisfiedAge.categorySubcategoryMap) {
        Object.entries(mostSatisfiedAge.categorySubcategoryMap).forEach(([category, subcatData]) => {
          Object.entries(subcatData.positive).forEach(([subcat, count]) => {
            enrichedPositiveSubcats.push({ 
              name: subcat, 
              count, 
              category: category.replace(/_/g, ' ')
            });
          });
        });
      }
      
      // Get subcategories with their parent categories for least satisfied age group
      if (leastSatisfiedAge.categorySubcategoryMap) {
        Object.entries(leastSatisfiedAge.categorySubcategoryMap).forEach(([category, subcatData]) => {
          Object.entries(subcatData.negative).forEach(([subcat, count]) => {
            enrichedNegativeSubcats.push({ 
              name: subcat, 
              count, 
              category: category.replace(/_/g, ' ')
            });
          });
        });
      }
      
      // Sort and take top 2
      enrichedPositiveSubcats.sort((a, b) => b.count - a.count);
      enrichedNegativeSubcats.sort((a, b) => b.count - a.count);
      
      // Build structured insight data instead of plain text
      const insightData = {
        summary: {
          mostSatisfied: {
            ageGroup: mostSatisfiedAge.ageGroup,
            percentage: mostSatisfiedAge.positivePercentage
          },
          leastSatisfied: {
            ageGroup: leastSatisfiedAge.ageGroup,
            percentage: leastSatisfiedAge.positivePercentage
          }
        },
        categories: {
          positive: mostSatisfiedAge.keyThemes.positiveCategories,
          negative: leastSatisfiedAge.keyThemes.negativeCategories
        },
        subcategories: {
          positive: enrichedPositiveSubcats.slice(0, 5),
          negative: enrichedNegativeSubcats.slice(0, 5)
        }
      };
      
      insights.push({
        type: 'age_satisfaction',
        title: 'Age Group Satisfaction Pattern',
        insightData: insightData, // Use structured data instead of plain text
        impact: Math.abs(mostSatisfiedAge.positivePercentage - leastSatisfiedAge.positivePercentage) > 20 ? 'High' : 'Moderate'
      });
    }

    // Generate comprehensive category-subcategory mapping
    const categorySubcategoryMap = {};
    allFeedback.forEach(item => {
      if (item.processed_data?.categories && item.processed_data?.subcategories) {
        item.processed_data.categories.forEach(cat => {
          const categoryName = cat.category;
          if (!categorySubcategoryMap[categoryName]) {
            categorySubcategoryMap[categoryName] = {
              positive: { subcategories: {}, total: 0 },
              negative: { subcategories: {}, total: 0 }
            };
          }
          
          // Find related subcategories for this category in this item
          item.processed_data.subcategories.forEach(subcat => {
            if (subcat.category === categoryName && subcat.subcategory !== 'Others') {
              const subcategoryName = subcat.subcategory.replace(/_/g, ' ');
              const sentiment = subcat.sentiment;
              
              if (sentiment === 'positive' || sentiment === 'negative') {
                if (!categorySubcategoryMap[categoryName][sentiment].subcategories[subcategoryName]) {
                  categorySubcategoryMap[categoryName][sentiment].subcategories[subcategoryName] = 0;
                }
                categorySubcategoryMap[categoryName][sentiment].subcategories[subcategoryName]++;
                categorySubcategoryMap[categoryName][sentiment].total++;
              }
            }
          });
        });
      }
    });

    // Convert to array format for display
    const categorySubcategoryArray = Object.entries(categorySubcategoryMap).map(([categoryName, data]) => {
      const positiveSubcats = Object.entries(data.positive.subcategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));
      
      const negativeSubcats = Object.entries(data.negative.subcategories)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));
      
      return {
        category: categoryName.replace(/_/g, ' '),
        positiveSubcategories: positiveSubcats,
        negativeSubcategories: negativeSubcats,
        positiveTotal: data.positive.total,
        negativeTotal: data.negative.total
      };
    }).sort((a, b) => (b.positiveTotal + b.negativeTotal) - (a.positiveTotal + a.negativeTotal));

    return {
      ageGroupSatisfaction,
      genderSatisfaction,
      insights,
      totalFeedback: allFeedback.length,
      categorySubcategoryMap: categorySubcategoryArray
    };
  };

  const analyzeGlobalInteractions = (allFeedback) => {
    const interactionMap = {};
    const interactionSentiments = {};
    const interactionKeyThemes = {};
    
    allFeedback.forEach(item => {
      const interaction = item.interactionSubCategory || 'Unknown';
      
      interactionMap[interaction] = (interactionMap[interaction] || 0) + 1;
      
      if (!interactionSentiments[interaction]) {
        interactionSentiments[interaction] = { positive: 0, negative: 0, neutral: 0 };
        interactionKeyThemes[interaction] = { 
          positiveCategories: {}, 
          negativeCategories: {}, 
          positiveSubcategories: {}, 
          negativeSubcategories: {} 
        };
      }
      
      // Process categories and subcategories for this interaction
      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          const categoryName = cat.category;
          const sentiment = cat.sentiment;
          
          interactionSentiments[interaction][sentiment]++;
          
          // Track key themes by category
          if (sentiment === 'positive') {
            interactionKeyThemes[interaction].positiveCategories[categoryName] = (interactionKeyThemes[interaction].positiveCategories[categoryName] || 0) + 1;
          } else if (sentiment === 'negative') {
            interactionKeyThemes[interaction].negativeCategories[categoryName] = (interactionKeyThemes[interaction].negativeCategories[categoryName] || 0) + 1;
          }
        });
      }

      // Process subcategories
      if (item.processed_data?.subcategories) {
        item.processed_data.subcategories.forEach(subcat => {
          if (subcat.subcategory && subcat.subcategory !== 'Others') {
            const subcategoryName = subcat.subcategory.replace(/_/g, ' ');
            const sentiment = subcat.sentiment;
            
            if (sentiment === 'positive') {
              interactionKeyThemes[interaction].positiveSubcategories[subcategoryName] = (interactionKeyThemes[interaction].positiveSubcategories[subcategoryName] || 0) + 1;
            } else if (sentiment === 'negative') {
              interactionKeyThemes[interaction].negativeSubcategories[subcategoryName] = (interactionKeyThemes[interaction].negativeSubcategories[subcategoryName] || 0) + 1;
            }
          }
        });
      }
    });

    const totalFeedback = allFeedback.length;
    const allInteractions = Object.entries(interactionMap)
      .sort((a, b) => b[1] - a[1])
      .map(([interaction, count]) => {
        const sentiments = interactionSentiments[interaction];
        
        // Calculate exact percentages
        const exactPositive = (sentiments.positive / count) * 100;
        const exactNegative = (sentiments.negative / count) * 100;
        const exactNeutral = (sentiments.neutral / count) * 100;
        
        // Round percentages ensuring they sum to 100
        let positivePercentage = Math.round(exactPositive);
        let negativePercentage = Math.round(exactNegative);
        let neutralPercentage = Math.round(exactNeutral);
        
        // Adjust for rounding errors to ensure sum = 100
        const sum = positivePercentage + negativePercentage + neutralPercentage;
        if (sum !== 100) {
          const diff = 100 - sum;
          // Adjust the largest percentage
          if (exactPositive >= exactNegative && exactPositive >= exactNeutral) {
            positivePercentage += diff;
          } else if (exactNegative >= exactNeutral) {
            negativePercentage += diff;
          } else {
            neutralPercentage += diff;
          }
        }
        
        // Get top key themes for this interaction
        const positiveCategories = Object.entries(interactionKeyThemes[interaction].positiveCategories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([cat, count]) => ({ name: cat.replace(/_/g, ' '), count }));
        
        const negativeCategories = Object.entries(interactionKeyThemes[interaction].negativeCategories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([cat, count]) => ({ name: cat.replace(/_/g, ' '), count }));
        
        const positiveSubcategories = Object.entries(interactionKeyThemes[interaction].positiveSubcategories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([subcat, count]) => ({ name: subcat, count }));
        
        const negativeSubcategories = Object.entries(interactionKeyThemes[interaction].negativeSubcategories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([subcat, count]) => ({ name: subcat, count }));
        
        return {
          interaction,
          count,
          percentage: Math.round((count / totalFeedback) * 100),
          positive: sentiments.positive,
          negative: sentiments.negative,
          neutral: sentiments.neutral,
          positivePercentage,
          negativePercentage,
          neutralPercentage,
          satisfactionLevel: positivePercentage > 60 ? 'High' : positivePercentage > 40 ? 'Medium' : 'Low',
          keyThemes: {
            positiveCategories,
            negativeCategories,
            positiveSubcategories,
            negativeSubcategories
          }
        };
      });

    // Generate global interaction insights
    const insights = [];
    const avgSatisfaction = Math.round(allInteractions.reduce((sum, int) => sum + int.positivePercentage, 0) / allInteractions.length);
    const problematicChannels = allInteractions.filter(channel => 
      channel.count > 10 && channel.positivePercentage < 40
    );

    if (allInteractions.length > 0) {
      const bestChannel = allInteractions[0];
      const worstChannel = allInteractions[allInteractions.length - 1];
      
      let insightText = `Analysis of ${allInteractions.length} interaction channels shows average satisfaction of ${avgSatisfaction}%. `;
      insightText += `Best performer: ${bestChannel.interaction} (${bestChannel.positivePercentage}%), `;
      insightText += `needs attention: ${worstChannel.interaction} (${worstChannel.positivePercentage}%).`;
      
      if (problematicChannels.length > 0) {
        insightText += ` ${problematicChannels.length} high-volume channels show concerning satisfaction levels.`;
      }
      
      insights.push({
        type: 'interaction_summary',
        title: 'Global Interaction Channel Performance',
        insight: insightText,
        impact: problematicChannels.length > 2 || worstChannel.positivePercentage < 30 ? 'High' : 
                avgSatisfaction < 50 ? 'Moderate' : 'Positive'
      });
    }

    // Generate comprehensive category-subcategory mapping for interactions
    const interactionCategoryMap = {};
    allFeedback.forEach(item => {
      const interaction = item.interactionSubCategory || 'Unknown';
      if (!interactionCategoryMap[interaction]) {
        interactionCategoryMap[interaction] = {};
      }
      
      if (item.processed_data?.categories && item.processed_data?.subcategories) {
        item.processed_data.categories.forEach(cat => {
          const categoryName = cat.category;
          if (!interactionCategoryMap[interaction][categoryName]) {
            interactionCategoryMap[interaction][categoryName] = {
              positive: { subcategories: {}, total: 0 },
              negative: { subcategories: {}, total: 0 }
            };
          }
          
          // Find related subcategories for this category in this item
          item.processed_data.subcategories.forEach(subcat => {
            if (subcat.category === categoryName && subcat.subcategory !== 'Others') {
              const subcategoryName = subcat.subcategory.replace(/_/g, ' ');
              const sentiment = subcat.sentiment;
              
              if (sentiment === 'positive' || sentiment === 'negative') {
                if (!interactionCategoryMap[interaction][categoryName][sentiment].subcategories[subcategoryName]) {
                  interactionCategoryMap[interaction][categoryName][sentiment].subcategories[subcategoryName] = 0;
                }
                interactionCategoryMap[interaction][categoryName][sentiment].subcategories[subcategoryName]++;
                interactionCategoryMap[interaction][categoryName][sentiment].total++;
              }
            }
          });
        });
      }
    });

    return {
      interactions: allInteractions,
      insights,
      avgSatisfaction,
      interactionCategoryMap
    };
  };

  if (!data || !data.data) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p>Loading global analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-8">
      {/* Global Demographics Analysis */}
      <div className="bg-purple-50 rounded-lg border border-purple-200">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-purple-100 transition-colors"
          onClick={() => setDemographicsExpanded(!demographicsExpanded)}
        >
          <div className="flex items-center">
            <IoPeopleOutline className="w-5 h-5 text-purple-600 mr-2" />
            <h4 className="text-lg font-semibold text-gray-800">Demographics Analysis (All Categories)</h4>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-sm flex items-center space-x-4">
              {globalDemographics ? (
                globalDemographics.ageGroupSatisfaction.map((ageData, idx) => (
                  <span key={idx} className="text-xs text-gray-600">
                    {ageData.ageGroup}: <span className="text-green-600 font-medium">{ageData.positivePercentage}% positive</span>
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-600">
                  Analyzing global demographic patterns...
                </span>
              )}
            </div>
            <motion.div
              animate={{ rotate: demographicsExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <IoArrowDownOutline className="w-4 h-4 text-gray-500" />
            </motion.div>
          </div>
        </div>
        
        {/* Collapsible Content */}
        <motion.div
          initial={false}
          animate={{ 
            height: demographicsExpanded ? 'auto' : '0px',
            opacity: demographicsExpanded ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="p-4 pt-0">
            {globalDemographics ? (
              <>
                {/* Demographic Insights */}
                {globalDemographics.insights && globalDemographics.insights.length > 0 && (
                  <div className="mb-4 space-y-3">
                    {/* Age Group Satisfaction Pattern - Full Width Row */}
                    {globalDemographics.insights.filter(insight => insight.type === 'age_satisfaction').map((insight, idx) => (
                      <div key={`age-satisfaction-${idx}`} className="bg-white p-4 rounded-lg border border-purple-100 w-full">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center flex-1">
                            <h5 className="font-medium text-gray-800 text-base">{insight.title}</h5>
                            <InfoPopover
                              title={`${insight.title} Formula`}
                              formula={getInsightFormula(insight.type)}
                              isOpen={activePopover === `global-demo-age-${idx}`}
                              onToggle={() => setActivePopover(activePopover === `global-demo-age-${idx}` ? null : `global-demo-age-${idx}`)}
                            />
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            insight.impact === 'High' ? 'bg-red-100 text-red-700' :
                            insight.impact === 'Moderate' ? 'bg-orange-100 text-orange-700' :
                            insight.impact === 'Positive' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {insight.impact}
                          </span>
                        </div>
                        {/* Enhanced visual rendering for Age Group Satisfaction Pattern */}
                        {insight.type === 'age_satisfaction' && insight.insightData ? (
                          <div className="space-y-3">
                            {/* Summary */}
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md font-medium">
                                {insight.insightData.summary.mostSatisfied.ageGroup}
                              </span>
                              <span className="text-gray-600">shows highest satisfaction</span>
                              <span className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs font-medium">
                                {insight.insightData.summary.mostSatisfied.percentage}% positive
                              </span>
                              <span className="text-gray-600">while</span>
                              <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md font-medium">
                                {insight.insightData.summary.leastSatisfied.ageGroup}
                              </span>
                              <span className="text-gray-600">shows lowest</span>
                              <span className="bg-red-50 text-red-700 px-2 py-1 rounded text-xs font-medium">
                                {insight.insightData.summary.leastSatisfied.percentage}% positive
                              </span>
                            </div>

                            {/* Categories */}
                            {(insight.insightData.categories.positive.length > 0 || insight.insightData.categories.negative.length > 0) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Positive Categories */}
                                {insight.insightData.categories.positive.length > 0 && (
                                  <div className="bg-green-50 rounded-lg p-3">
                                    <div className="flex items-center mb-2">
                                      <span className="text-green-700 font-medium text-sm mr-2">
                                        💪 {insight.insightData.summary.mostSatisfied.ageGroup} excels in:
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {insight.insightData.categories.positive.map((cat, idx) => (
                                        <span key={idx} className="bg-green-200 text-green-800 px-2 py-1 rounded-md text-xs font-medium">
                                          📊 {cat.name} (+{cat.count})
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Negative Categories */}
                                {insight.insightData.categories.negative.length > 0 && (
                                  <div className="bg-red-50 rounded-lg p-3">
                                    <div className="flex items-center mb-2">
                                      <span className="text-red-700 font-medium text-sm mr-2">
                                        ⚠️ {insight.insightData.summary.leastSatisfied.ageGroup} struggles with:
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {insight.insightData.categories.negative.map((cat, idx) => (
                                        <span key={idx} className="bg-red-200 text-red-800 px-2 py-1 rounded-md text-xs font-medium">
                                          📊 {cat.name} (-{cat.count})
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Subcategories */}
                            {(insight.insightData.subcategories.positive.length > 0 || insight.insightData.subcategories.negative.length > 0) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Positive Subcategories */}
                                {insight.insightData.subcategories.positive.length > 0 && (
                                  <div className="bg-blue-50 rounded-lg p-3">
                                                                         <div className="flex items-center mb-2">
                                       <span className="text-blue-700 font-medium text-sm mr-2">
                                         ⭐ Top positive subcategories for {insight.insightData.summary.mostSatisfied.ageGroup}:
                                       </span>
                                     </div>
                                                                         <div className="flex flex-wrap gap-1">
                                       {insight.insightData.subcategories.positive.map((sub, idx) => (
                                         <span key={idx} className="bg-blue-200 text-blue-800 px-2 py-1 rounded-md text-xs font-medium">
                                           🔧 {sub.name} {sub.category && `[${sub.category}]`} (+{sub.count})
                                         </span>
                                       ))}
                                     </div>
                                  </div>
                                )}

                                {/* Negative Subcategories */}
                                {insight.insightData.subcategories.negative.length > 0 && (
                                  <div className="bg-orange-50 rounded-lg p-3">
                                                                         <div className="flex items-center mb-2">
                                       <span className="text-orange-700 font-medium text-sm mr-2">
                                         🚨 Problem subcategories for {insight.insightData.summary.leastSatisfied.ageGroup}:
                                       </span>
                                     </div>
                                                                         <div className="flex flex-wrap gap-1">
                                       {insight.insightData.subcategories.negative.map((sub, idx) => (
                                         <span key={idx} className="bg-orange-200 text-orange-800 px-2 py-1 rounded-md text-xs font-medium">
                                           🔧 {sub.name} {sub.category && `[${sub.category}]`} (-{sub.count})
                                         </span>
                                       ))}
                                     </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-700 leading-relaxed">{insight.insight}</p>
                        )}
                      </div>
                    ))}
                    
                    {/* Other Insights in Grid */}
                    {globalDemographics.insights.filter(insight => insight.type !== 'age_satisfaction').length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {globalDemographics.insights.filter(insight => insight.type !== 'age_satisfaction').map((insight, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border border-purple-100">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center flex-1">
                                <h5 className="font-medium text-gray-800 text-sm">{insight.title}</h5>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700">{insight.insight}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Age and Gender Data with Key Themes */}
                <div className="space-y-6">
                  {/* Age Group Satisfaction */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Age Group Satisfaction</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {globalDemographics.ageGroupSatisfaction.map((ageData, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">{ageData.ageGroup}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {ageData.total}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded ${
                                ageData.positivePercentage > 60 
                                  ? 'bg-green-100 text-green-800'
                                  : ageData.positivePercentage > 40 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {ageData.positivePercentage}%
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex space-x-1 mb-2">
                            <div 
                              className="bg-green-500 h-1.5 rounded-l"
                              style={{ width: `${ageData.positivePercentage}%` }}
                            ></div>
                            <div 
                              className="bg-yellow-500 h-1.5"
                              style={{ width: `${ageData.neutralPercentage}%` }}
                            ></div>
                            <div 
                              className="bg-red-500 h-1.5 rounded-r"
                              style={{ width: `${ageData.negativePercentage}%` }}
                            ></div>
                          </div>
                          
                          <div className="flex justify-between text-xs text-gray-500 mb-2">
                            <span className="text-green-600">+{ageData.positive}</span>
                            <span className="text-yellow-600">~{ageData.neutral}</span>
                            <span className="text-red-600">-{ageData.negative}</span>
                          </div>

                          {/* Enhanced Key Themes with Subcategories */}
                          {ageData.keyThemes && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              {/* Category-level themes */}
                              <div className="text-xs text-gray-600 mb-2">
                                {ageData.keyThemes.positiveCategories.length > 0 && (
                                  <div className="mb-1">
                                    <span className="font-medium text-green-600">Strong Categories:</span>{' '}
                                    {ageData.keyThemes.positiveCategories.map(cat => `${cat.name} (+${cat.count})`).join(', ')}
                                  </div>
                                )}
                                {ageData.keyThemes.negativeCategories.length > 0 && (
                                  <div className="mb-1">
                                    <span className="font-medium text-red-600">Problem Categories:</span>{' '}
                                    {ageData.keyThemes.negativeCategories.map(cat => `${cat.name} (-${cat.count})`).join(', ')}
                                  </div>
                                )}
                              </div>
                              
                              {/* Strong and Problem Subcategories */}
                              {ageData.categorySubcategoryMap && Object.keys(ageData.categorySubcategoryMap).length > 0 && (
                                <div className="text-xs bg-gray-50 rounded p-2">
                                  <div className="font-medium text-gray-700 mb-2">Strong and Problem Subcategories:</div>
                                  <div className="space-y-3 max-h-48 overflow-y-auto">
                                    {/* Collect all positive subcategories with their categories */}
                                    {(() => {
                                      const allPositiveSubcats = [];
                                      const allNegativeSubcats = [];
                                      
                                      Object.entries(ageData.categorySubcategoryMap).forEach(([category, subcatData]) => {
                                        Object.entries(subcatData.positive).forEach(([subcat, count]) => {
                                          allPositiveSubcats.push({ subcat, count, category: category.replace(/_/g, ' ') });
                                        });
                                        Object.entries(subcatData.negative).forEach(([subcat, count]) => {
                                          allNegativeSubcats.push({ subcat, count, category: category.replace(/_/g, ' ') });
                                        });
                                      });
                                      
                                      // Sort by count and take top items
                                      allPositiveSubcats.sort((a, b) => b.count - a.count);
                                      allNegativeSubcats.sort((a, b) => b.count - a.count);
                                      
                                      return (
                                        <div className="space-y-2">
                                          {/* Strong Subcategories */}
                                          {allPositiveSubcats.length > 0 && (
                                            <div className="bg-white border border-gray-200 rounded p-2">
                                              <div className="text-green-600 font-medium text-xs mb-1">✓ Strong Subcategories:</div>
                                              <div className="grid grid-cols-1 gap-1">
                                                {allPositiveSubcats.slice(0, 5).map((item, idx) => (
                                                  <div key={idx} className="flex justify-between items-center text-xs bg-green-50 px-2 py-1 rounded">
                                                    <span className="text-gray-700 truncate">
                                                      {item.subcat} <span className="text-gray-500">[{item.category}]</span>
                                                    </span>
                                                    <span className="text-green-600 font-medium">+{item.count}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Problem Subcategories */}
                                          {allNegativeSubcats.length > 0 && (
                                            <div className="bg-white border border-gray-200 rounded p-2">
                                              <div className="text-red-600 font-medium text-xs mb-1">✗ Problem Subcategories:</div>
                                              <div className="grid grid-cols-1 gap-1">
                                                {allNegativeSubcats.slice(0, 5).map((item, idx) => (
                                                  <div key={idx} className="flex justify-between items-center text-xs bg-red-50 px-2 py-1 rounded">
                                                    <span className="text-gray-700 truncate">
                                                      {item.subcat} <span className="text-gray-500">[{item.category}]</span>
                                                    </span>
                                                    <span className="text-red-600 font-medium">-{item.count}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Gender Satisfaction */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Gender Satisfaction</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {globalDemographics.genderSatisfaction.map((genderData, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">{genderData.gender}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {genderData.total}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded ${
                                genderData.positivePercentage > 60 
                                  ? 'bg-green-100 text-green-800'
                                  : genderData.positivePercentage > 40 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {genderData.positivePercentage}%
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex space-x-1 mb-2">
                            <div 
                              className="bg-green-500 h-1.5 rounded-l"
                              style={{ width: `${genderData.positivePercentage}%` }}
                            ></div>
                            <div 
                              className="bg-yellow-500 h-1.5"
                              style={{ width: `${genderData.neutralPercentage}%` }}
                            ></div>
                            <div 
                              className="bg-red-500 h-1.5 rounded-r"
                              style={{ width: `${genderData.negativePercentage}%` }}
                            ></div>
                          </div>
                          
                          <div className="flex justify-between text-xs text-gray-500 mb-2">
                            <span className="text-green-600">+{genderData.positive}</span>
                            <span className="text-yellow-600">~{genderData.neutral}</span>
                            <span className="text-red-600">-{genderData.negative}</span>
                          </div>

                          {/* Enhanced Key Themes with Subcategories */}
                          {genderData.keyThemes && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              {/* Category-level themes */}
                              <div className="text-xs text-gray-600 mb-2">
                                {genderData.keyThemes.positiveCategories.length > 0 && (
                                  <div className="mb-1">
                                    <span className="font-medium text-green-600">Strong Categories:</span>{' '}
                                    {genderData.keyThemes.positiveCategories.map(cat => `${cat.name} (+${cat.count})`).join(', ')}
                                  </div>
                                )}
                                {genderData.keyThemes.negativeCategories.length > 0 && (
                                  <div className="mb-1">
                                    <span className="font-medium text-red-600">Problem Categories:</span>{' '}
                                    {genderData.keyThemes.negativeCategories.map(cat => `${cat.name} (-${cat.count})`).join(', ')}
                                  </div>
                                )}
                              </div>
                              
                              {/* Subcategory-level details */}
                              {(genderData.keyThemes.positiveSubcategories.length > 0 || genderData.keyThemes.negativeSubcategories.length > 0) && (
                                <div className="text-xs bg-gray-50 rounded p-2">
                                  <div className="font-medium text-gray-700 mb-1">Key Subcategory Insights:</div>
                                  {genderData.keyThemes.positiveSubcategories.length > 0 && (
                                    <div className="mb-1">
                                      <span className="font-medium text-green-600">✓ Positive:</span>{' '}
                                      <span className="text-gray-600">
                                        {genderData.keyThemes.positiveSubcategories.map(sub => `${sub.name} (+${sub.count})`).join(', ')}
                                      </span>
                                    </div>
                                  )}
                                  {genderData.keyThemes.negativeSubcategories.length > 0 && (
                                    <div>
                                      <span className="font-medium text-red-600">✗ Negative:</span>{' '}
                                      <span className="text-gray-600">
                                        {genderData.keyThemes.negativeSubcategories.map(sub => `${sub.name} (-${sub.count})`).join(', ')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>


              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Loading global demographics analysis...
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Global Channel Performance Analysis */}
      <div className="bg-green-50 rounded-lg border border-green-200">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-green-100 transition-colors"
          onClick={() => setInteractionExpanded(!interactionExpanded)}
        >
          <div className="flex items-center">
            <IoBusinessOutline className="w-5 h-5 text-green-600 mr-2" />
            <h4 className="text-lg font-semibold text-gray-800">Channel Performance (All Categories)</h4>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-sm text-gray-600">
              {globalInteractions ? (
                <span className="text-xs">
                  {globalInteractions.interactions.length} channels | 
                  Avg: <span className="text-green-600 font-medium">{globalInteractions.avgSatisfaction}% positive</span>
                </span>
              ) : (
                <span className="text-xs">
                  Analyzing global channel performance...
                </span>
              )}
            </div>
            <motion.div
              animate={{ rotate: interactionExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <IoArrowDownOutline className="w-4 h-4 text-gray-500" />
            </motion.div>
          </div>
        </div>
        
        {/* Collapsible Content */}
        <motion.div
          initial={false}
          animate={{ 
            height: interactionExpanded ? 'auto' : '0px',
            opacity: interactionExpanded ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="p-4 pt-0">
            {globalInteractions ? (
              <>
                {/* Channel Performance Insights */}
                {globalInteractions.insights && globalInteractions.insights.length > 0 && (
                  <div className="mb-4 space-y-3">
                    {/* Global Interaction Channel Performance - Full Width Row */}
                    {globalInteractions.insights.filter(insight => insight.type === 'interaction_summary').map((insight, idx) => (
                      <div key={`interaction-summary-${idx}`} className="bg-white p-4 rounded-lg border border-green-100 w-full">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center flex-1">
                            <h5 className="font-medium text-gray-800 text-base">{insight.title}</h5>
                            <InfoPopover
                              title={`${insight.title} Formula`}
                              formula={getInsightFormula(insight.type)}
                              isOpen={activePopover === `global-inter-summary-${idx}`}
                              onToggle={() => setActivePopover(activePopover === `global-inter-summary-${idx}` ? null : `global-inter-summary-${idx}`)}
                            />
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            insight.impact === 'Critical' ? 'bg-red-100 text-red-700' :
                            insight.impact === 'High' ? 'bg-orange-100 text-orange-700' :
                            insight.impact === 'Positive' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {insight.impact}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{insight.insight}</p>
                      </div>
                    ))}
                    
                    {/* Other Insights in Grid */}
                    {globalInteractions.insights.filter(insight => insight.type !== 'interaction_summary').length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {globalInteractions.insights.filter(insight => insight.type !== 'interaction_summary').map((insight, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border border-green-100">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center flex-1">
                                <h5 className="font-medium text-gray-800 text-sm">{insight.title}</h5>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700">{insight.insight}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Global Channel Performance List with Key Themes */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="text-sm font-medium text-gray-700">
                      All Channels ({globalInteractions.interactions.length})
                    </h5>
                    <div className="flex bg-white rounded-lg border border-gray-200 p-1">
                      <button
                        onClick={() => setInteractionSortBy('positive')}
                        className={`px-3 py-1 text-xs rounded-md transition-all ${
                          interactionSortBy === 'positive'
                            ? 'bg-green-100 text-green-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        % Positive
                      </button>
                      <button
                        onClick={() => setInteractionSortBy('negative')}
                        className={`px-3 py-1 text-xs rounded-md transition-all ${
                          interactionSortBy === 'negative'
                            ? 'bg-red-100 text-red-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        % Negative
                      </button>
                    </div>
                  </div>
                  
                  {/* Single Sorted Channel List with Key Themes */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {(() => {
                      const sortedChannels = [...globalInteractions.interactions].sort((a, b) => 
                        interactionSortBy === 'positive' 
                          ? b.positivePercentage - a.positivePercentage
                          : b.negativePercentage - a.negativePercentage
                      );
                      
                      return sortedChannels.map((interaction, idx) => (
                        <div key={idx} className="bg-white rounded border border-gray-200 hover:border-gray-300 transition-colors p-3">
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex-1">
                              <span className="text-sm font-medium text-gray-700">{interaction.interaction}</span>
                              <div className="flex items-center space-x-4 mt-1">
                                <span className="text-xs text-gray-500">{interaction.count} interactions</span>
                                <div className="flex items-center space-x-2 text-xs">
                                  <span className="text-green-600">{interaction.positivePercentage}% pos</span>
                                  <span className="text-yellow-600">{interaction.neutralPercentage}% neu</span>
                                  <span className="text-red-600">{interaction.negativePercentage}% neg</span>
                                </div>
                              </div>
                            </div>
                            <span className={`text-xs px-3 py-1 rounded font-medium ${
                              interactionSortBy === 'positive'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {interactionSortBy === 'positive' ? interaction.positivePercentage : interaction.negativePercentage}%
                            </span>
                          </div>

                          {/* Enhanced Key Themes with Subcategories for this channel */}
                          {interaction.keyThemes && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              {/* Category-level themes */}
                              <div className="text-xs text-gray-600 mb-2">
                                {interaction.keyThemes.positiveCategories.length > 0 && (
                                  <div className="mb-1">
                                    <span className="font-medium text-green-600">Strong Categories:</span>{' '}
                                    {interaction.keyThemes.positiveCategories.map(cat => `${cat.name} (+${cat.count})`).join(', ')}
                                  </div>
                                )}
                                {interaction.keyThemes.negativeCategories.length > 0 && (
                                  <div className="mb-1">
                                    <span className="font-medium text-red-600">Problem Categories:</span>{' '}
                                    {interaction.keyThemes.negativeCategories.map(cat => `${cat.name} (-${cat.count})`).join(', ')}
                                  </div>
                                )}
                              </div>
                              
                              {/* Enhanced Subcategory Performance with Categories */}
                              {interaction.keyThemes && (
                                <div className="text-xs bg-gray-50 rounded p-2">
                                  <div className="font-medium text-gray-700 mb-2">Subcategory Performance:</div>
                                  <div className="space-y-2">
                                    {/* Collect all subcategories with their categories for this interaction */}
                                    {(() => {
                                      const allPositiveSubcats = [];
                                      const allNegativeSubcats = [];
                                      
                                      // Get subcategories with category context from interactionCategoryMap
                                      if (globalInteractions.interactionCategoryMap && globalInteractions.interactionCategoryMap[interaction.interaction]) {
                                        Object.entries(globalInteractions.interactionCategoryMap[interaction.interaction]).forEach(([category, subcatData]) => {
                                          Object.entries(subcatData.positive.subcategories || {}).forEach(([subcat, count]) => {
                                            allPositiveSubcats.push({ subcat, count, category: category.replace(/_/g, ' ') });
                                          });
                                          Object.entries(subcatData.negative.subcategories || {}).forEach(([subcat, count]) => {
                                            allNegativeSubcats.push({ subcat, count, category: category.replace(/_/g, ' ') });
                                          });
                                        });
                                      }
                                      
                                      // Sort by count
                                      allPositiveSubcats.sort((a, b) => b.count - a.count);
                                      allNegativeSubcats.sort((a, b) => b.count - a.count);
                                      
                                      return (
                                        <div className="space-y-2">
                                          {/* Positive Subcategories */}
                                          {allPositiveSubcats.length > 0 && (
                                            <div>
                                              <div className="text-green-600 font-medium text-xs mb-1">✓ Excels in:</div>
                                              <div className="text-gray-600 text-xs">
                                                {allPositiveSubcats.slice(0, 5).map((item, idx) => 
                                                  `${item.subcat} [${item.category}] (+${item.count})`
                                                ).join(', ')}
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* Negative Subcategories */}
                                          {allNegativeSubcats.length > 0 && (
                                            <div>
                                              <div className="text-red-600 font-medium text-xs mb-1">✗ Struggles with:</div>
                                              <div className="text-gray-600 text-xs">
                                                {allNegativeSubcats.slice(0, 5).map((item, idx) => 
                                                  `${item.subcat} [${item.category}] (-${item.count})`
                                                ).join(', ')}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                                      </div>
                  </div>

                  
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Loading global channel performance analysis...
                </div>
              )}
            </div>
          </motion.div>
        </div>
    </div>
  );
};

export default GlobalAnalytics; 